<script setup>
import PageWrapper from '@/components/shared/page/PageWrapper.vue'
import ChatInterface from '@/components/chat/ChatInterface.vue'
import EnrollmentActions from '@/components/enrollments/EnrollmentActions.vue'
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import Avatar from '@/components/shared/core/Avatar.vue'
import {myEnrollmentMessagesApi} from '@/api/my-enrollment-messages.api.js'
import {usePage} from '@/composables/use-page.js'
import {useRouting} from '@/composables/use-routing.js'
import {useLoading} from '@/composables/use-loading.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useEnrollmentMessagesQuerying} from '@/composables/use-enrollment-messages-querying.js'
import {useChannelManagement} from '@/composables/use-channel-management.js'
import {useEnrollmentManagement} from '@/composables/use-enrollment-management.js'
import {useLastSeenMessage} from '@/composables/use-last-seen-message.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useAuthentication} from '@/composables/use-authentication.js'
import {useTeams} from '@/composables/use-teams.js'
import {useSegment} from '@/composables/use-segment.js'
import {ref, computed, onMounted, onUnmounted} from 'vue'

//Setup page
const {setTitle} = usePage()
setTitle('My enrollment')

//Composables
const {processError} = useErrorHandling()
const {isLoading, setLoading} = useLoading(true)
const {routeParams, queryParams, replaceRoute} = useRouting()
const {allowUsersToManageEnrollments} = useCurrentOrganization()
const {goToHome, goToManageEnrollment} = useNavigation()
const {redirectToLogout} = useAuthentication()
const {isInTeams} = useTeams()
const {track} = useSegment()
const {enrollmentId, channelId} = routeParams.value
const scrollToEnrollmentId = ref(queryParams.value.enrollmentId)
const scrollToMessageId = ref(queryParams.value.messageId)
const showLogoutButton = computed(() => isChatDisabled.value && !isInTeams.value)

//Enrollment messages querying (enrollment and channel are obtained from this endpoint)
const {
  enrollment,
  messages,
  channel,
  loadMessages,
  updateMessages,
  startPollingMessages,
  stopPollingMessages,
} = useEnrollmentMessagesQuerying(channelId, enrollmentId)

//Enrollment management
const {
  updateIsUnread: updateEnrollmentIsUnread,
  startOrStopEnrollment,
} = useEnrollmentManagement(enrollment)

//Channel management
const {
  updateIsUnread: updateChannelIsUnread,
} = useChannelManagement(channel)

//Message seen tracking
const {
  lastSeenMessageId,
  trackMessageSeen,
} = useLastSeenMessage(channelId, enrollmentId)

//User message handler
const processUserMessage = async message => {
  try {
    const data = message.getCreateJson()
    await myEnrollmentMessagesApi.create(enrollment.value.id, data)

    //Track with Segment
    track('User Responded', {
      channelId,
      enrollmentId,
      externalMessageId: message.externalId,
    })

    //Reset polling timer, and load new messages after a short delay
    startPollingMessages()
    setTimeout(loadMessages, 2500)
  }
  catch (error) {
    message.error = error
    updateMessages()
    processError(error)
  }
}

//Computed to check if the chat is disabled
const isChatDisabled = computed(() => {
  if (!enrollment.value?.canRespond) {
    return true
  }
  if (messages.value.length === 0) {
    return true
  }
  return false
})

//Disabled reason
const disabledReason = computed(() => {
  if (enrollment.value?.hasNotStartedYet || messages.value.length === 0) {
    return `This enrollment hasn't started yet`
  }
  else if (enrollment.value?.isCompleted) {
    return `You're all finished!`
  }
  else if (enrollment.value?.isCanceled) {
    return `You've canceled this enrollment`
  }
  else if (enrollment.value?.isOptedOut) {
    return `You've opted out of this enrollment`
  }
  else if (enrollment.value?.isDisabled) {
    return `This enrollment has been disabled`
  }
  else if (enrollment.value?.isError || enrollment.value?.isWhatsappWindowViolated) {
    return `An error occurred during this enrollment`
  }
  return ''
})

//Helper to mark either the channel or enrollment as read.
//NOTE: This is just for marking the whole channel/enrollment as read when we
//land on this view. For manually toggling an enrollment back as unread, we
//always only toggle the (last) enrollment in the channel.
//NOTE: We used to check if there were any unread enrollments, but because there
//could be enrollments that aren't returned and which are unread, this would
//prevent this update from being called. So since it's a pretty cheap call,
//we'll just call it every time for channels.
const markAsRead = () => {
  if (channel.value) {
    updateChannelIsUnread(false)
    if (enrollment.value) {
      enrollment.value.isUnread = false
    }
  }
  else if (enrollment.value?.id) {
    updateEnrollmentIsUnread(false)
  }
}

//Helper wrapper to mark the enrollment as unread
//NOTE: This only applies to the (last) enrollment, not the whole channel
//After marking as unread, we also redirect back to the home view
const markAsUnread = async () => {
  try {
    await updateEnrollmentIsUnread(true)
    goToHome()
  }
  catch {
    //Ignore
  }
}

//On mount hook
onMounted(async () => {

  //Load enrollment and messages
  try {

    //Load messages and enrollment metadata (comes from same endpoint)
    await loadMessages()

    //If we're supposed to scroll to an enrollment ID, find the first corresponding
    //message and set it's ID on the scrollToMessageId ref to leverage existing logic
    if (scrollToEnrollmentId.value) {
      const message = messages.value.find(message => message.enrollmentId === scrollToEnrollmentId.value)
      if (message) {
        scrollToMessageId.value = message.id
      }
    }

    //Clear loading state
    setLoading(false)
  }
  catch (error) {
    processError(error)
    replaceRoute('home')
  }

  //Start polling messages
  startPollingMessages()

  //Mark as read as we're viewing the channel/enrollment now
  try {
    markAsRead()
  }
  catch {
    //Ignore
  }
})

//On unmount hook
onUnmounted(stopPollingMessages)
</script>

<template>
  <PageWrapper
    is-fixed-height
    can-go-back
    @back="goToHome()"
  >
    <template #header>
      <Avatar
        v-if="channel && channel.signedImageUrl"
        :image="channel.signedImageUrl"
        :image-alt-text="channel.name"
        size="normal"
      />
      <h1 :class="$style.displayName">
        {{ channel?.name || enrollment?.displayName }}
      </h1>
    </template>
    <template #actions>
      <EnrollmentActions
        v-if="enrollment"
        :is-unread="enrollment.isUnread"
        :is-paused="enrollment.isPaused"
        :can-manage="allowUsersToManageEnrollments"
        :can-toggle-unread="!enrollment.hasEnded"
        :can-toggle-paused="allowUsersToManageEnrollments && enrollment.canPauseResume"
        @toggle-unread="markAsUnread()"
        @toggle-paused="startOrStopEnrollment()"
        @manage="goToManageEnrollment(enrollment.id)"
      />
    </template>

    <Spinner
      v-if="isLoading"
      is-block
    />

    <template v-else>
      <ChatInterface
        :messages="messages"
        :scroll-to-message-id="scrollToMessageId"
        :last-seen-message-id="lastSeenMessageId"
        :channel="channel"
        :is-chat-disabled="isChatDisabled || isLoading"
        :show-course-names="!!channel"
        show-timestamps
        show-new
        @message="processUserMessage($event.message)"
        @message-seen="trackMessageSeen($event.message)"
      >
        <template #after-messages>
          <small
            v-if="isChatDisabled && disabledReason"
            text-muted
            :class="$style.disabledReason"
          >{{ disabledReason }}</small>
        </template>
      </ChatInterface>

      <div :class="$style.buttons">
        <ActionButton
          v-if="isChatDisabled"
          type="primary"
          label="View enrollments"
          @click="goToHome()"
        />
        <ActionButton
          v-if="showLogoutButton"
          type="text"
          label="Logout"
          @click="redirectToLogout()"
        />
      </div>
    </template>
  </PageWrapper>
</template>

<style module>
.displayName {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: var(--font-size-heading-xs);
  font-weight: var(--font-weight-bold);
  @media (min-width: 768px) {
    font-size: var(--font-size-heading-s);
  }
}
.disabledReason {
  display: block;
  margin: 2rem auto;
  text-align: center;
}
.buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
